// jQuery
let $ = require('jquery');
window.$ = $;
window.jQuery = $;
window.Popper = require('popper.js');

// It fails to detect jQuery when loaded with `import`. So make sure to check there are no errors in console after refactoring this.
require('magnific-popup');

require('trix');

import 'phoenix_html';

import { Socket } from 'phoenix';
import { LiveSocket } from 'phoenix_live_view';

import topbar from 'topbar';

import './alpine';

import { isMobile } from './utils/breakpoint';
import { closestPhxBinding } from './utils/dom';
import { debounce } from './utils/debounce';

import './polyfills/check_visibility';

import Params from './params';

import S3Uploader from './uploaders/s3_uploader';

import * as Sentry from '@sentry/browser';

import DropdownHook from './hooks/dropdown_hook';
import FormCheckboxDropdownHook from './hooks/form_checkbox_dropdown_hook';
import FormFileInputDropTargetHook from './hooks/form_file_input_drop_target_hook';
import FormTextareaAutosizeHook from './hooks/form_textarea_autosize_hook';
import TooltipHook from './hooks/tooltip_hook';
import StickyObserverHook from './hooks/sticky_observer_hook';
import SmoothScrollToHook from './hooks/smooth_scroll_to_hook';
import LineClampHook from './hooks/line_clamp_hook';
import ModalHook from './hooks/modal_hook';
import CarouselHook from './hooks/carousel_hook';
import LightboxHook from './hooks/lightbox_hook';
import ChoicesHook from './hooks/choices_hook';
import ChartHook from './hooks/chart_hook';
import SortableHook from './hooks/sortable_hook';
import SearchPaletteHook from './hooks/search_palette_hook';
import ToggleHook from './hooks/toggle_hook';
import FormToggleInputHook from './hooks/form_toggle_input_hook';
import ShareHook from './hooks/share_hook';
import ListingsMapHook from './hooks/listings_map_hook';
import ListingsItemHook from './hooks/listings_item_hook';
import SignaturePadHook from './hooks/signature_pad_hook';
import FormMaskedInputHook from './hooks/form_masked_input_hook';
import LeaveConfirmationHook from './hooks/leave_confirmation_hook';
import ListingDateSliderHook from './hooks/listing_date_slider_hook';
import OverflowBoxHook from './hooks/overflow_box_hook';
import FormScopedResetHook from './hooks/form_scoped_reset_hook';
import FieldBreakpointHook from './hooks/field_breakpoint_hook';

import ListingMapHook from './hooks/listing_map_hook';
import BuildingItemHook from './hooks/building_item_hook';
import BuildingsListHook from './hooks/buildings_list_hook';
import LandlordBuildingsMapHook from './hooks/landlord_buildings_map_hook';
import PaymentElementHook from './hooks/payment_element_hook';
import NeighborhoodMapHook from './hooks/neighborhood_map_hook';

let Hooks = {
  FormCheckboxDropdownHook,
  FormFileInputDropTargetHook,
  FormTextareaAutosizeHook,
  TooltipHook,
  StickyObserverHook,
  SmoothScrollToHook,
  LineClampHook,
  ModalHook,
  CarouselHook,
  LightboxHook,
  ChoicesHook,
  ChartHook,
  SortableHook,
  SearchPaletteHook,
  ShareHook,
  DropdownHook,
  SignaturePadHook,
  FormMaskedInputHook,
  LeaveConfirmationHook,
  OverflowBoxHook,
  FormScopedResetHook,
  FieldBreakpointHook,

  ListingsMapHook,
  ListingsItemHook,
  ListingMapHook,
  BuildingsListHook,
  BuildingItemHook,
  LandlordBuildingsMapHook,
  PaymentElementHook,
  ListingDateSliderHook,
  NeighborhoodMapHook,

  // Deprecated
  ToggleHook,
  FormToggleInputHook,
};

window.ENV = ENV;
window.DEPLOYMEN_ENV = DEPLOYMENT_ENV;
window.RELEASE = RELEASE;

if (ENV == 'prod') {
  Sentry.init({
    dsn: 'https://f2efd2fcb15cad4b97d449035b155791@o103309.ingest.us.sentry.io/237502',
    environment: DEPLOYMENT_ENV,

    // Alternatively, use `process.env.npm_package_version` for a dynamic release version
    // if your build tool supports it.
    release: RELEASE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: ['localhost'],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const params = () => {
  const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content');

  const transferParams = Object.fromEntries(
    [...document.querySelectorAll('meta[data-param-transfer]')].map((meta) => [
      meta.name,
      meta.content,
    ]),
  );

  const params = {
    _csrf_token: csrfToken,
    'window-width': window.innerWidth,
    'window-height': window.innerHeight,
    paramsTransfer: transferParams,
    params: Params.get(),
  };

  return params;
};

// HACK Remove once LV will suport navigation callbacks.
// It should be placed before LiveSocket initialization so we can't place it in the hook.
window.addEventListener('click', function (e) {
  const originalTarget = e.target;

  if (!window.CONFIRM_LEAVE_EL) {
    return;
  }

  let target = closestPhxBinding(e.target, 'data-phx-link');
  let type = target && target.getAttribute('data-phx-link');

  // FIXME: won't work for `patch` but it's often used for modal forms
  if (type === 'redirect') {
    e.preventDefault();
    e.stopImmediatePropagation();

    const showEvent = {
      detail: {
        onConfirm: () => {
          originalTarget.dispatchEvent(new Event('click', { bubbles: true }));
        },
      },
    };

    window.CONFIRM_LEAVE_EL.dispatchEvent(new CustomEvent('show', showEvent));
  }
});

let liveSocket = new LiveSocket('/live', Socket, {
  params: params,
  uploaders: { S3: S3Uploader },
  hooks: Hooks,
  dom: {
    onBeforeElUpdated(from, to) {
      if (from._x_dataStack) {
        window.Alpine.clone(from, to);
        // window.Alpine.initTree(to);
      }

      for (const attr of from.attributes) {
        if (attr.name.startsWith('data-js-')) {
          to.setAttribute(attr.name, attr.value);
        }
      }
    },
  },
});

// Show progress bar on live navigation and form submits
topbar.config({
  barColors: { 0: '#29d' },
  shadowColor: 'rgba(0, 0, 0, .3)',
  className: 'topbar',
});

window.addEventListener('phx:page-loading-start', (e) => {
  // We need to clear params so they are kept per liveview only.
  // Otherwise we can get corrupted state like on apply page when you navigate to other
  //  page, then back to Apply and we trying to recover invalid step.
  if (e.detail.kind == 'initial') {
    Params.clear();
  }

  topbar.show(300);
});

window.addEventListener('phx:page-loading-stop', (e) => {
  topbar.hide();
});

window.addEventListener('phx:store', ({ detail }) => {
  localStorage.setItem(detail.key, JSON.stringify(detail.data));
});

window.addEventListener('phx:reset-window-scroll', () => {
  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
  });
});

window.addEventListener('phx:smooth-scroll', ({ detail }) => {
  const target = document.querySelector(detail.to);

  target.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
});

window.addEventListener(`phx:reset-scroll`, (e) => {
  let el = document.getElementById(e.detail.id);

  if (el) {
    el.scrollTop = 0;
    el.scrollLeft = 0;
  }
});

window.addEventListener('phx:history-back', () => {
  history.back();
});

window.addEventListener('phx:js-exec', ({ detail }) => {
  document.querySelectorAll(detail.to).forEach((el) => {
    liveSocket.execJS(el, el.getAttribute(detail.attr));
  });
});

window.addEventListener('phx:set-param', ({ detail }) => {
  Params.set(detail.namespace, detail.key, detail.value);
});

window.addEventListener('phx:dispatch', ({ detail }) => {
  const event = new CustomEvent(detail.event, { detail: detail.detail, bubles: detail.bubbles });
  const el = document.querySelector(detail.to);

  el && el.dispatchEvent(event);
});

window.addEventListener('phx:live_reload:attached', ({ detail: reloader }) => {
  // enable server log streaming to client.
  // disable with reloader.disableServerLogs()
  reloader.enableServerLogs();
});

const windowSizeListener = debounce(() => {
  liveSocket.execJSHookPush(document.body, 'screen:resize', {
    width: window.innerWidth,
    height: window.innerHeight,
  });
}, 200);

window.addEventListener('resize', windowSizeListener);

// Trix editor does not trigger input event when content pasted but only when you input
document.addEventListener('trix-paste', (event) => {
  const editorEl = event.target;
  const inputEl = document.getElementById(editorEl.getAttribute('input'));

  inputEl.dispatchEvent(new Event('input', { bubbles: true }));
});

liveSocket.connect();
liveSocket.enableProfiling();

window.liveSocket = liveSocket;
